import { StripeElements, Stripe } from '@stripe/stripe-js';
import { CardElement } from '@stripe/react-stripe-js';
import { ApiResponse } from 'apisauce';
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { ErrorAction, OkAction } from './baseActions';
import { billingService } from '../services/billingAPI';
import {
  Subscription,
  BasePlan,
  PaymentMethod,
  Invoice,
  Usage,
  Limits,
  BillingInformation,
} from '../types/resources';
import { download } from '../utils/fileHelper';
import { RobocloudError } from '../types/common';
import queryClient from '../queryClient';
import { getOrganizationsQueryKey } from '~/queries/organization';
import { formatApiErrorPayload } from './utils';

type AppState = import('~/store').AppState;
type WithId = { id: string };

export enum BillingActionType {
  GET_PLANS = 'GET_PLANS',
  GET_PLANS_OK = 'GET_PLANS_OK',
  GET_PLANS_ERROR = 'GET_PLANS_ERROR',
  GET_CURRENT_SUBSCRIPTION = 'GET_CURRENT_SUBSCRIPTION',
  GET_CURRENT_SUBSCRIPTION_OK = 'GET_CURRENT_SUBSCRIPTION_OK',
  GET_CURRENT_SUBSCRIPTION_ERROR = 'GET_CURRENT_SUBSCRIPTION_ERROR',
  GET_CURRENT_PAYMENT_METHOD = 'GET_CURRENT_PAYMENT_METHOD',
  GET_CURRENT_PAYMENT_METHOD_OK = 'GET_CURRENT_PAYMENT_METHOD_OK',
  GET_CURRENT_PAYMENT_METHOD_ERROR = 'GET_CURRENT_PAYMENT_METHOD_ERROR',
  CHANGE_SUBSCRIPTION_TIER = 'CHANGE_SUBSCRIPTION_TIER',
  CHANGE_SUBSCRIPTION_TIER_OK = 'CHANGE_SUBSCRIPTION_TIER_OK',
  CHANGE_SUBSCRIPTION_TIER_ERROR = 'CHANGE_SUBSCRIPTION_TIER_ERROR',
  CREATE_PAYMENT_METHOD = 'CREATE_PAYMENT_METHOD',
  CREATE_PAYMENT_METHOD_OK = 'CREATE_PAYMENT_METHOD_OK',
  CREATE_PAYMENT_METHOD_ERROR = 'CREATE_PAYMENT_METHOD_ERROR',
  UPDATE_BILLING_INFORMATION = 'UPDATE_BILLING_INFORMATION',
  UPDATE_BILLING_INFORMATION_OK = 'UPDATE_BILLING_INFORMATION_OK',
  UPDATE_BILLING_INFORMATION_ERROR = 'UPDATE_BILLING_INFORMATION_ERROR',
  DELETE_PAYMENT_METHOD = 'DELETE_PAYMENT_METHOD',
  DELETE_PAYMENT_METHOD_OK = 'DELETE_PAYMENT_METHOD_OK',
  DELETE_PAYMENT_METHOD_ERROR = 'DELETE_PAYMENT_METHOD_ERROR',
  LIST_INVOICES = 'LIST_INVOICES',
  LIST_INVOICES_OK = 'LIST_INVOICES_OK',
  LIST_INVOICES_ERROR = 'LIST_INVOICES_ERROR',
  GET_INVOICE_FILE = 'GET_INVOICE_FILE',
  GET_INVOICE_FILE_OK = 'GET_INVOICE_FILE_OK',
  GET_INVOICE_FILE_ERROR = 'GET_INVOICE_FILE_ERROR',
  GET_USAGE = 'GET_USAGE',
  GET_USAGE_OK = 'GET_USAGE_OK',
  GET_USAGE_ERROR = 'GET_USAGE_ERROR',
  UPDATE_USAGE_LIMIT = 'UPDATE_USAGE_LIMIT',
  UPDATE_USAGE_LIMIT_OK = 'UPDATE_USAGE_LIMIT_OK',
  UPDATE_USAGE_LIMIT_ERROR = 'UPDATE_USAGE_LIMIT_ERROR',
}

type GetPlansAction =
  | Action<BillingActionType.GET_PLANS>
  | OkAction<BillingActionType.GET_PLANS_OK, BasePlan[]>
  | ErrorAction<BillingActionType.GET_PLANS_ERROR>;

type GetCurrentSubscriptionAction =
  | Action<BillingActionType.GET_CURRENT_SUBSCRIPTION>
  | OkAction<BillingActionType.GET_CURRENT_SUBSCRIPTION_OK, Subscription>
  | ErrorAction<BillingActionType.GET_CURRENT_SUBSCRIPTION_ERROR>;

type GetCurrentPaymentMethodAction =
  | Action<BillingActionType.GET_CURRENT_PAYMENT_METHOD>
  | OkAction<BillingActionType.GET_CURRENT_PAYMENT_METHOD_OK, PaymentMethod>
  | ErrorAction<BillingActionType.GET_CURRENT_PAYMENT_METHOD_ERROR>;

type ChangeSubscriptionTierAction =
  | Action<BillingActionType.CHANGE_SUBSCRIPTION_TIER>
  | OkAction<BillingActionType.CHANGE_SUBSCRIPTION_TIER_OK, Subscription>
  | ErrorAction<BillingActionType.CHANGE_SUBSCRIPTION_TIER_ERROR>;

type CreatePaymentMethodAction =
  | Action<BillingActionType.CREATE_PAYMENT_METHOD>
  | OkAction<BillingActionType.CREATE_PAYMENT_METHOD_OK, PaymentMethod>
  | ErrorAction<BillingActionType.CREATE_PAYMENT_METHOD_ERROR>;

type UpdateBillingInformationAction =
  | Action<BillingActionType.UPDATE_BILLING_INFORMATION>
  | OkAction<BillingActionType.UPDATE_BILLING_INFORMATION_OK, any>
  | ErrorAction<BillingActionType.UPDATE_BILLING_INFORMATION_ERROR>;

type DeletePaymentMethodAction =
  | Action<BillingActionType.DELETE_PAYMENT_METHOD>
  | OkAction<BillingActionType.DELETE_PAYMENT_METHOD_OK, PaymentMethod>
  | ErrorAction<BillingActionType.DELETE_PAYMENT_METHOD_ERROR>;

type GetInvoicesAction =
  | Action<BillingActionType.LIST_INVOICES>
  | OkAction<BillingActionType.LIST_INVOICES_OK, Invoice[]>
  | ErrorAction<BillingActionType.LIST_INVOICES_ERROR>;

type GetInvoiceFileAction =
  | (Action<BillingActionType.GET_INVOICE_FILE> & WithId)
  | (OkAction<BillingActionType.GET_INVOICE_FILE_OK, Invoice> & WithId)
  | (ErrorAction<BillingActionType.GET_INVOICE_FILE_ERROR> & WithId);

type GetUsageAction =
  | Action<BillingActionType.GET_USAGE>
  | OkAction<BillingActionType.GET_USAGE_OK, Usage>
  | ErrorAction<BillingActionType.GET_USAGE_ERROR>;

type UpdateMinuteLimitAction =
  | Action<BillingActionType.UPDATE_USAGE_LIMIT>
  | OkAction<BillingActionType.UPDATE_USAGE_LIMIT_OK, Usage>
  | ErrorAction<BillingActionType.UPDATE_USAGE_LIMIT_ERROR>;

export type BillingAction =
  | GetPlansAction
  | GetCurrentSubscriptionAction
  | ChangeSubscriptionTierAction
  | CreatePaymentMethodAction
  | GetCurrentPaymentMethodAction
  | UpdateBillingInformationAction
  | DeletePaymentMethodAction
  | GetUsageAction
  | UpdateMinuteLimitAction
  | GetInvoicesAction
  | GetInvoiceFileAction;

export const getPlans =
  (): ThunkAction<void, AppState, null, GetPlansAction> => async (dispatch) => {
    dispatch({ type: BillingActionType.GET_PLANS });
    const response = await billingService.getPlans();

    if (response.ok) {
      dispatch({
        type: BillingActionType.GET_PLANS_OK,
        data: response.data as BasePlan[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: BillingActionType.GET_PLANS_ERROR,
      });
    }
  };

export const getCurrentSubscription =
  (): ThunkAction<void, AppState, null, GetCurrentSubscriptionAction> =>
  async (dispatch, getState) => {
    dispatch({ type: BillingActionType.GET_CURRENT_SUBSCRIPTION });

    const organizationId = getState().ui.currentOrganizationId;
    const response = await billingService.getCurrentSubscription(organizationId);

    if (response.ok) {
      dispatch({
        type: BillingActionType.GET_CURRENT_SUBSCRIPTION_OK,
        data: response.data as Subscription,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: BillingActionType.GET_CURRENT_SUBSCRIPTION_ERROR,
      });
    }
  };

export const getCurrentPaymentMethod =
  (): ThunkAction<void, AppState, null, GetCurrentPaymentMethodAction> =>
  async (dispatch, getState) => {
    dispatch({ type: BillingActionType.GET_CURRENT_PAYMENT_METHOD });

    const organizationId = getState().ui.currentOrganizationId;
    const response = await billingService.getCurrentPaymentMethod(organizationId);

    if (response.ok) {
      dispatch({
        type: BillingActionType.GET_CURRENT_PAYMENT_METHOD_OK,
        data: response.data as PaymentMethod,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: BillingActionType.GET_CURRENT_PAYMENT_METHOD_ERROR,
      });
    }
  };

export const changeSubscriptionTier =
  (
    tierId: string,
  ): ThunkAction<
    Promise<ApiResponse<Subscription | RobocloudError>>,
    AppState,
    null,
    ChangeSubscriptionTierAction
  > =>
  async (dispatch, getState) => {
    dispatch({ type: BillingActionType.CHANGE_SUBSCRIPTION_TIER });

    const organizationId = getState().ui.currentOrganizationId;
    const payload = { tierId };
    const response = await billingService.updateSubscription(organizationId, payload);

    if (response.ok && response.data) {
      dispatch({
        type: BillingActionType.CHANGE_SUBSCRIPTION_TIER_OK,
        data: response.data as Subscription,
      });

      // Refresh organization state as limit_overrides will be out of date
      queryClient.invalidateQueries(getOrganizationsQueryKey());
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: BillingActionType.CHANGE_SUBSCRIPTION_TIER_ERROR,
      });
    }

    return response;
  };

export const createPaymentMethod =
  (
    stripe?: Stripe | null,
    elems?: StripeElements | null,
    details?: BillingInformation,
  ): ThunkAction<void, AppState, null, CreatePaymentMethodAction> =>
  async (dispatch, getState) => {
    dispatch({ type: BillingActionType.CREATE_PAYMENT_METHOD });
    const organizationId = getState().ui.currentOrganizationId;

    if (!stripe || !elems) {
      dispatch({
        type: BillingActionType.CREATE_PAYMENT_METHOD_ERROR,
        error: 'No Stripe and/or Stripe Elements provided',
      });

      return;
    }

    if (!details) {
      dispatch({
        type: BillingActionType.CREATE_PAYMENT_METHOD_ERROR,
        error: 'No details provided',
      });

      return;
    }

    const { name, email, address } = details;
    const card = elems.getElement(CardElement);

    if (!card) {
      dispatch({
        type: BillingActionType.CREATE_PAYMENT_METHOD_ERROR,
        error: 'No card element provided',
      });

      return;
    }

    // Create payment method to Stripe
    const createStripePaymentMethod = await stripe.createPaymentMethod({
      type: 'card',
      card,
      billing_details: {
        name,
        email,
        address: {
          line1: address.line1,
          line2: address.line2,
          postal_code: address.postalCode,
          city: address.city,
          state: address.state,
          country: address.country,
        },
      },
    });

    if (createStripePaymentMethod.error || !createStripePaymentMethod.paymentMethod) {
      dispatch({
        type: BillingActionType.CREATE_PAYMENT_METHOD_ERROR,
        error: createStripePaymentMethod?.error?.message || '',
      });
    } else {
      const { paymentMethod } = createStripePaymentMethod;

      // Pass created payment method ID and organization ID to billing service
      const configurePaymentMethodResult = await billingService.createPaymentMethod(
        organizationId,
        {
          paymentMethodId: paymentMethod.id,
          businessId: details.businessId,
        },
      );
      if (configurePaymentMethodResult.ok) {
        dispatch({
          type: BillingActionType.CREATE_PAYMENT_METHOD_OK,
          data: configurePaymentMethodResult.data as PaymentMethod,
        });
      } else {
        dispatch({
          ...formatApiErrorPayload(configurePaymentMethodResult),
          type: BillingActionType.CREATE_PAYMENT_METHOD_ERROR,
        });
      }
    }
  };

export const updateBillingInformation =
  (
    update: Partial<BillingInformation>,
  ): ThunkAction<void, AppState, null, UpdateBillingInformationAction> =>
  async (dispatch, getState) => {
    const organizationId = getState().ui.currentOrganizationId;
    dispatch({ type: BillingActionType.UPDATE_BILLING_INFORMATION });

    const response = await billingService.updateBillingInformation(organizationId, update);

    if (response.ok) {
      dispatch({
        type: BillingActionType.UPDATE_BILLING_INFORMATION_OK,
        data: response.data?.billingInformation,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: BillingActionType.UPDATE_BILLING_INFORMATION_ERROR,
      });
    }
  };

export const deletePaymentMethod =
  (): ThunkAction<void, AppState, null, DeletePaymentMethodAction> =>
  async (dispatch, getState) => {
    const organizationId = getState().ui.currentOrganizationId;
    dispatch({ type: BillingActionType.DELETE_PAYMENT_METHOD, organizationId });

    const response = await billingService.deletePaymentMethod(organizationId);
    if (response.ok) {
      dispatch({
        type: BillingActionType.DELETE_PAYMENT_METHOD_OK,
        data: response.data as PaymentMethod,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: BillingActionType.DELETE_PAYMENT_METHOD_ERROR,
      });
    }
  };

export const getInvoices =
  (): ThunkAction<void, AppState, null, GetInvoicesAction> => async (dispatch, getState) => {
    const organizationId = getState().ui.currentOrganizationId;
    dispatch({ type: BillingActionType.LIST_INVOICES, organizationId });

    const response = await billingService.getInvoices(organizationId);
    if (response.ok) {
      dispatch({
        type: BillingActionType.LIST_INVOICES_OK,
        data: response.data as Invoice[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: BillingActionType.LIST_INVOICES_ERROR,
      });
    }
  };

export const getInvoiceFile =
  (invoiceId: string): ThunkAction<void, AppState, null, GetInvoiceFileAction> =>
  async (dispatch, getState) => {
    const organizationId = getState().ui.currentOrganizationId;
    dispatch({ type: BillingActionType.GET_INVOICE_FILE, id: invoiceId });

    const response = await billingService.getInvoiceFile(organizationId, invoiceId);
    if (response.ok) {
      const { content, fileName, contentType } = response.data as any;
      // Trigger file download
      download(content, { fileName, encoding: 'base64', contentType });

      dispatch({
        type: BillingActionType.GET_INVOICE_FILE_OK,
        id: invoiceId,
        data: response.data as Invoice,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: BillingActionType.GET_INVOICE_FILE_ERROR,
        id: invoiceId,
      });
    }
  };

export const getUsage =
  (orgId?: string): ThunkAction<void, AppState, null, GetUsageAction> =>
  async (dispatch, getState) => {
    const organizationId = orgId || getState().ui.currentOrganizationId;
    dispatch({ type: BillingActionType.GET_USAGE, organizationId });

    const response = await billingService.getUsage(organizationId);
    if (response.ok) {
      dispatch({
        type: BillingActionType.GET_USAGE_OK,
        data: response.data as Usage,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: BillingActionType.GET_USAGE_ERROR,
        showSnackbar: false,
      });
    }
  };

export const updateUsageLimit =
  (update: Partial<Limits>): ThunkAction<Promise<void>, AppState, null, UpdateMinuteLimitAction> =>
  async (dispatch, getState) => {
    const organizationId = getState().ui.currentOrganizationId;
    dispatch({ type: BillingActionType.UPDATE_USAGE_LIMIT });

    const response = await billingService.updateUsageLimit(organizationId, update);
    if (response.ok) {
      dispatch({
        type: BillingActionType.UPDATE_USAGE_LIMIT_OK,
        data: response.data as Usage,
      });

      // Refresh organization state after updating minute limit
      queryClient.invalidateQueries(getOrganizationsQueryKey());
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: BillingActionType.UPDATE_USAGE_LIMIT_ERROR,
      });
    }
  };
